import { useEffect, useState } from "react";
import { Play, Pause, Loader2, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Lecteur audio compact + téléchargement, partagé par la page Appels et la
 * bibliothèque de Formation. L'audio (blob authentifié) n'est chargé qu'au 1er
 * clic sur « play », via le proxy `/calls/{id}/recording`. Une seule lecture à la
 * fois sur la page (évite la cacophonie sur une liste).
 */

function fmtClock(s) {
  if (!s && s !== 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// Une seule lecture inline active à la fois.
let CURRENT_INLINE_AUDIO = null;

/**
 * Télécharge l'enregistrement audio d'un appel (blob authentifié via l'API).
 * Nom de fichier lisible ; extension déduite du type MIME reçu.
 */
export async function downloadCallRecording(callId, fileLabel) {
  // `dl=true` : le backend renvoie un MP3 mono léger, facile à partager sur WhatsApp.
  const res = await api.get(`/calls/${callId}/recording?dl=true`, { responseType: "blob" });
  const blob = res.data;
  const ct = blob.type || res.headers?.["content-type"] || "audio/mpeg";
  const ext = /wav/i.test(ct) ? "wav" : "mp3";
  const safe = String(fileLabel || "appel").replace(/[^\w\-]+/g, "_").replace(/_+/g, "_").slice(0, 70) || "appel";
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${safe}.${ext}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function CallRecordingPlayer({ callId, totalSec, onPlay, fileLabel }) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(totalSec || 0);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const audioRef = useState(() => (typeof Audio !== "undefined" ? new Audio() : null))[0];

  useEffect(() => {
    const a = audioRef;
    if (!a) return;
    const onTime = () => setCur(a.currentTime || 0);
    const onMeta = () => { if (isFinite(a.duration)) setDur(a.duration); };
    const onEnd = () => { setPlaying(false); setCur(0); };
    const onPause = () => setPlaying(false);
    const onPlayEv = () => setPlaying(true);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onPause);
    a.addEventListener("play", onPlayEv);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("play", onPlayEv);
      try { a.pause(); } catch { /* noop */ }
      if (a.src) { URL.revokeObjectURL(a.src); a.removeAttribute("src"); }
      if (CURRENT_INLINE_AUDIO === a) CURRENT_INLINE_AUDIO = null;
    };
    // eslint-disable-next-line
  }, []);

  const toggle = async () => {
    const a = audioRef;
    if (!a) return;
    if (playing) { a.pause(); return; }
    onPlay?.();
    if (CURRENT_INLINE_AUDIO && CURRENT_INLINE_AUDIO !== a) { try { CURRENT_INLINE_AUDIO.pause(); } catch { /* noop */ } }
    CURRENT_INLINE_AUDIO = a;
    if (!a.src) {
      setLoading(true); setError(false);
      try {
        const res = await api.get(`/calls/${callId}/recording`, { responseType: "blob" });
        a.src = URL.createObjectURL(res.data);
      } catch {
        setError(true); setLoading(false);
        toast.error("Enregistrement indisponible.");
        return;
      }
      setLoading(false);
    }
    try { await a.play(); } catch { /* autoplay guard */ }
  };

  const seek = (e) => {
    const a = audioRef;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * dur;
    setCur(a.currentTime);
  };

  const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;

  const download = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadCallRecording(callId, fileLabel);
      toast.success("Enregistrement téléchargé.");
    } catch {
      toast.error("Téléchargement impossible.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-inline-player className="flex w-full items-center gap-2" data-testid={`inline-player-${callId}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        disabled={loading}
        aria-label={playing ? "Pause" : "Lecture"}
        data-testid={`inline-play-${callId}`}
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--shariti-orange)] text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-[1px]" />}
      </button>
      <div
        onClick={(e) => { e.stopPropagation(); seek(e); }}
        className="group relative h-2 flex-1 cursor-pointer rounded-full bg-[var(--surface-2)]"
        data-testid={`inline-seek-${callId}`}
      >
        <div className="absolute inset-y-0 left-0 rounded-full bg-[var(--shariti-orange)]" style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-ink" data-testid={`inline-time-${callId}`}>
        {error ? "indispo." : `${fmtClock(cur)} / ${fmtClock(dur)}`}
      </span>
      <button
        type="button"
        onClick={download}
        disabled={downloading}
        title="Télécharger l'audio de l'appel"
        aria-label="Télécharger l'enregistrement"
        data-testid={`inline-download-${callId}`}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--shariti-orange)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)] disabled:opacity-60"
      >
        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
